/**
 * Admissions API.
 *
 * ── THE SERVER DECIDES WHAT IS OFFERED ───────────────────────────────────────────────
 *
 * `admission` arrives on the event payload already resolved: which action is available,
 * what the party range is, what state the event is in. Nothing here re-derives it from the
 * mode, because a second opinion computed in the browser eventually disagrees with the
 * server and the browser's copy is the wrong one.
 */

import { api } from './apiClient';

export type AdmissionMode = 'ticketed' | 'rsvp' | 'request' | 'invite_only' | 'donation';

export type AdmissionState =
  | 'ticketed' | 'rsvp_open' | 'request_open' | 'invitation_required'
  | 'full' | 'full_waitlist_open' | 'not_open_yet' | 'closed'
  | 'event_not_public' | 'mode_unavailable' | 'unavailable';

/** What the public event payload carries. Deliberately narrow — no capacity, no counts. */
export interface PublicAdmission {
  mode: AdmissionMode;
  preset: string | null;
  state: AdmissionState;
  canBuy: boolean;
  canRsvp: boolean;
  canRequest: boolean;
  canJoinWaitlist: boolean;
  requiresInvitation: boolean;
  partySizeMin: number;
  partySizeMax: number;
}

export interface AttendeeInput {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string | null;
}

export interface SubmissionResult {
  admissionId?: string;
  kind: 'request' | 'waitlist' | 'rsvp' | 'invitation';
  status?: string;
  confirmed?: boolean;
  orderId?: string;
  partySize: number;
  accessToken: string | null;
  idempotentReplay?: boolean;
}

export interface AdmissionStatus {
  kind: 'request' | 'waitlist';
  id: string;
  eventId: string;
  status: string;
  partySize: number;
  submittedAt?: string;
  joinedAt?: string;
  offerExpiresAt?: string | null;
  /** Present only on a decline, and only when an operator wrote one. Never an internal note. */
  message?: string | null;
  orderId?: string | null;
  event: { id: string; title: string; date: string | null };
  mode: AdmissionMode;
}

export const getEventAdmission = (eventId: string) =>
  api.get<PublicAdmission>(`/admissions/events/${eventId}`);

export const submitRequest = (body: {
  eventId: string; partySize: number; idempotencyKey: string;
} & AttendeeInput) => api.post<SubmissionResult>('/admissions/requests', body);

export const confirmRsvp = (body: {
  eventId: string; partySize: number; idempotencyKey: string;
} & AttendeeInput) => api.post<SubmissionResult>('/admissions/rsvp', body);

export const joinWaitlist = (body: {
  eventId: string; partySize: number; idempotencyKey: string;
} & AttendeeInput) => api.post<SubmissionResult>('/admissions/waitlist', body);

export const redeemInvitation = (body: {
  eventId: string; token: string; partySize: number;
} & AttendeeInput) => api.post<SubmissionResult>('/admissions/invitations/redeem', body);

export const getAdmissionStatus = (admissionId: string, token: string | null) =>
  api.get<AdmissionStatus>(
    `/admissions/${admissionId}/status${token ? `?token=${encodeURIComponent(token)}` : ''}`);

export const cancelAdmission = (admissionId: string, token: string | null) =>
  api.post<{ admissionId: string; status: string }>(
    `/admissions/${admissionId}/cancel`, { token });

/** Take up a waitlist offer. Capacity is checked again here, not at offer time. */
export const acceptOffer = (admissionId: string, token: string | null) =>
  api.post<{ admissionId: string; confirmed: boolean; orderId: string; accessToken: string | null }>(
    `/admissions/${admissionId}/accept`, { token });

/**
 * Where a submitted admission's credential is kept.
 *
 * ── localStorage, WITH ITS LIMITS ACKNOWLEDGED ───────────────────────────────────────
 *
 * A guest who submits a request gets a token once. Keeping it lets this browser show them
 * their status without asking them to dig the emailed link out. It is per-browser and
 * per-device — the email is the durable copy, and the page says so rather than implying the
 * token is safe here.
 */
const KEY = 'voleto.admissions';

type Stored = Record<string, { admissionId: string; token: string; kind: string }>;

export function rememberAdmission(eventId: string, value: Stored[string]): void {
  try {
    const all = readAll();
    all[eventId] = value;
    window.localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    // Private mode, blocked storage, a full quota. The emailed link still works, so a
    // forgotten token is an inconvenience rather than a lost admission.
  }
}

export function recallAdmission(eventId: string): Stored[string] | null {
  const all = readAll();
  return Object.hasOwn(all, eventId) ? all[eventId] : null;
}

function readAll(): Stored {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    // Own keys only: a stored blob is data from outside this code's control.
    return Object.fromEntries(Object.entries(parsed as Stored)) as Stored;
  } catch {
    return {};
  }
}
